import React from 'react';
import '../styles/IndicatorPanel.css';

const IndicatorPanel = ({ analysis, optionMode, marketSnapshot, symbol, support, resistance }) => {
  const unitsStr = optionMode === 'Index' ? 'in K' : 'in 10s';

  const formatValue = (value) => {
    if (value === undefined || value === null) return 'N/A';
    return typeof value === 'number' ? value.toFixed(2) : value;
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment === 'Over-Bullish') return '#ff9800';   // contrarian warning
    if (sentiment === 'Bullish')      return '#00e676';
    if (sentiment === 'Bearish'  || sentiment === 'Yes') return '#e53935';
    if (sentiment === 'Over-Bearish') return '#ff9800';   // contrarian warning
    return '#d9d9d9';
  };

  const getSentimentColorCallExits = (sentiment) => {
      if (sentiment === 'Yes') return '#00e676';
      return '#d9d9d9';
  };
  const getSentimentColorPutExits = (sentiment) => {
      if (sentiment === 'Yes') return '#e53935';
      return '#d9d9d9';
    };

  const getPCRColor = (pcr) => {
    if (pcr  > 1.4)  return '#ff9800';   // Over-Bullish — contrarian warning
    if (pcr  > 1.2)  return '#00e676';   // Bullish
    if (pcr  >= 0.8) return '#d9d9d9';   // Neutral
    if (pcr  >= 0.6) return '#e53935';   // Bearish
    return '#ff9800';                    // Over-Bearish — contrarian warning
  };

  const getSetupSignalColor = (signal) => {
    if (signal === 'Strong Bullish') return '#00c853';
    if (signal === 'Bullish')        return '#00e676';
    if (signal === 'Strong Bearish') return '#b71c1c';
    if (signal === 'Bearish')        return '#e53935';
    return '#ff9800';
  };

  const getMatrixColor = (matrix) => {
    if (matrix === 'Long Build-up'  || matrix === 'Short Covering')  return '#00e676';
    if (matrix === 'Short Build-up' || matrix === 'Long Unwinding') return '#e53935';
    return '#d9d9d9';
  };

  // Pain Point helpers
  const spot        = analysis.value;
  const maxPain     = analysis.maxPainPrice;
  const oiRangeLow  = Math.min(analysis.maxPutOISP || 0, analysis.maxCallOISP || 0);
  const oiRangeHigh = Math.max(analysis.maxPutOISP || 0, analysis.maxCallOISP || 0);

  const rangeSignal = (() => {
    if (!spot || !oiRangeLow || !oiRangeHigh) return { label: 'N/A', color: '#d9d9d9' };
    if (spot > oiRangeHigh) return { label: 'Above Resistance — Bullish Breakout', color: '#00e676' };
    if (spot < oiRangeLow)  return { label: 'Below Support — Bearish Breakdown',   color: '#e53935' };
    return { label: 'Inside OI Range — Await Breakout', color: '#ff9800' };
  })();

  const painSignal = (() => {
    if (!spot || !maxPain) return { label: 'N/A', color: '#d9d9d9' };
    if (spot > maxPain)    return { label: 'Above Pain — Bullish Bias',  color: '#00e676' };
    if (spot < maxPain)    return { label: 'Below Pain — Bearish Bias',  color: '#e53935' };
    return                        { label: 'At Pain Point',              color: '#ff9800' };
  })();

  // IV helpers
  const getIVRegimeColor = (regime) => {
    if (regime === 'Very Low')  return '#667eea';
    if (regime === 'Low')       return '#00e676';
    if (regime === 'Normal')    return '#d9d9d9';
    if (regime === 'High')      return '#ff9800';
    if (regime === 'Very High') return '#e53935';
    return '#d9d9d9';
  };

  const ivSkewSignal = (() => {
    const skew = analysis.ivSkew;
    if (skew == null) return { label: 'N/A', color: '#d9d9d9' };
    if (skew >  5)  return { label: 'Put Skew — Bearish Bias',  color: '#e53935' };
    if (skew >  2)  return { label: 'Slight Put Skew',           color: '#ff9800' };
    if (skew > -2)  return { label: 'Balanced',                  color: '#d9d9d9' };
    if (skew > -5)  return { label: 'Slight Call Skew',          color: '#ff9800' };
    return                 { label: 'Call Skew — Bullish Chase', color: '#ff9800' };
  })();
  const marketData = marketSnapshot || {};
  const openValue = Number(marketData.open);
  const previousCloseValue = Number(marketData.previousClose);
  const isOpenAbovePreviousClose =
    Number.isFinite(openValue) &&
    Number.isFinite(previousCloseValue) &&
    openValue > previousCloseValue;
  const isOpenBelowPreviousClose =
    Number.isFinite(openValue) &&
    Number.isFinite(previousCloseValue) &&
    openValue < previousCloseValue;
  const lastValue = Number(marketData.last);
  const highValue = Number(marketData.high);
  const lowValue = Number(marketData.low);
  const lastColor =
    Number.isFinite(lastValue) && Number.isFinite(highValue) && lastValue > highValue
      ? '#00e676'
      : Number.isFinite(lastValue) && Number.isFinite(lowValue) && lastValue < lowValue
        ? '#e53935'
        : '#d9d9d9';
  const resistanceValue = Number(resistance);
  const resistanceColor =
    Number.isFinite(lastValue) && Number.isFinite(resistanceValue) && lastValue > resistanceValue
      ? '#00e676'
      : '#d9d9d9';
  const supportValue = Number(support);
  const supportColor =
    Number.isFinite(lastValue) && Number.isFinite(supportValue) && lastValue < supportValue
      ? '#e53935'
      : '#d9d9d9';
  const formatLevel = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : 'N/A';
  };

  return (
    <div className="indicator-panel">
      {/* Open Interest Upper Boundary */}
      <div className="indicator-section">
        <h3>Open Interest Upper Boundary</h3>
        <div className="indicator-row">
          <div className="indicator-item">
            <label>Strike Price 1:</label>
            <span>{formatValue(analysis.maxCallOISP)}</span>
          </div>
          <div className="indicator-item">
            <label>OI ({unitsStr}):</label>
            <span>{formatValue(analysis.maxCallOI)}</span>
          </div>
        </div>
        <div className="indicator-row">
          <div className="indicator-item">
            <label>Strike Price 2:</label>
            <span>{formatValue(analysis.maxCallOISP2)}</span>
          </div>
          <div className="indicator-item">
            <label>OI ({unitsStr}):</label>
            <span>{formatValue(analysis.maxCallOI2)}</span>
          </div>
        </div>
      </div>

      {/* Open Interest Lower Boundary */}
      <div className="indicator-section">
        <h3>Open Interest Lower Boundary</h3>
        <div className="indicator-row">
          <div className="indicator-item">
            <label>Strike Price 1:</label>
            <span>{formatValue(analysis.maxPutOISP)}</span>
          </div>
          <div className="indicator-item">
            <label>OI ({unitsStr}):</label>
            <span>{formatValue(analysis.maxPutOI)}</span>
          </div>
        </div>
        <div className="indicator-row">
          <div className="indicator-item">
            <label>Strike Price 2:</label>
            <span>{formatValue(analysis.maxPutOISP2)}</span>
          </div>
          <div className="indicator-item">
            <label>OI ({unitsStr}):</label>
            <span>{formatValue(analysis.maxPutOI2)}</span>
          </div>
        </div>
      </div>

      <div className="indicator-section">
        <h3>{symbol || 'All Indicies'}</h3>
        <div className="indicator-row">
          <div className="indicator-item">
            <label>Open:</label>
            <span
              style={
                isOpenAbovePreviousClose
                  ? { backgroundColor: '#00e676', color: '#fff' }
                  : isOpenBelowPreviousClose
                    ? { backgroundColor: '#e53935', color: '#fff' }
                    : undefined
              }
            >
              {formatValue(marketData.open)}
            </span>
          </div>
          <div className="indicator-item">
            <label>High:</label>
            <span>{formatValue(marketData.high)}</span>
          </div>
        </div>
        <div className="indicator-row">
          <div className="indicator-item">
            <label>Low:</label>
            <span>{formatValue(marketData.low)}</span>
          </div>
          <div className="indicator-item">
            <label>Last:</label>
            <span style={{ backgroundColor: lastColor, color: '#fff' }}>{formatValue(marketData.last)}</span>
          </div>
        </div>
        <div className="indicator-row">
          <div className="indicator-item">
            <label>Previous Close:</label>
            <span>{formatValue(marketData.previousClose)}</span>
          </div>
          <div className="indicator-item">
            <label>VIX:</label>
            <span>{formatValue(marketData.vix)}</span>
          </div>
        </div>
      </div>

      <div className="indicator-section">
        <h3>Support / Resistance</h3>
        <div className="indicator-row">
          <div className="indicator-item">
            <label>Support:</label>
            <span style={{ backgroundColor: supportColor, color: '#fff' }}>{formatLevel(support)}</span>
          </div>
          <div className="indicator-item">
            <label>Resistance:</label>
            <span style={{ backgroundColor: resistanceColor, color: '#fff' }}>{formatLevel(resistance)}</span>
          </div>
        </div>
      </div>

      {/* Summary Indicators */}
      <div className="indicator-section summary">
        <div className="indicator-row">
          <div className="indicator-item">
            <label>Open Interest:</label>
            <span
                style={{ backgroundColor: getSentimentColor(analysis.totalOI) }}
                          className="sentiment-badge">
                          {analysis.totalOI}
            </span>
          </div>
          <div className="indicator-item">
            <label>PCR:</label>
            <span style={{backgroundColor: getPCRColor(analysis.pcr) }}
                                            className="sentiment-badge">
                                 {formatValue(analysis.pcr)}</span>
          </div>
        </div>
        <div className="indicator-row">
          <div className="indicator-item">
            <label>Call Exits:</label>
            <span
              style={{ backgroundColor: getSentimentColorCallExits(analysis.callExits) }}
              className="sentiment-badge"
            >
              {analysis.callExits}
            </span>
          </div>
          <div className="indicator-item">
            <label>Put Exits:</label>
            <span
            style={{ backgroundColor: getSentimentColorPutExits(analysis.putExits) }}
            className="sentiment-badge">
            {analysis.putExits}
            </span>
          </div>
        </div>
        <div className="indicator-row">
          <div className="indicator-item">
            <label>Call ITM:</label>
            <span
            style={{ backgroundColor: analysis.itmColorCall }}
                                     className="sentiment-badge">
            {analysis.callITM || 'No'}
            </span>
          </div>
          <div className="indicator-item">
            <label>Put ITM:</label>
            <span
             style={{ backgroundColor: analysis.itmColorPut }}
                         className="sentiment-badge">
            {analysis.putITM || 'No'}
            </span>
          </div>
        </div>
      </div>

      {/* Pain Point / OI Range */}
      {maxPain && (
        <div className="indicator-section pain-zone">
          <h3>Pain Point / OI Range</h3>
          <div className="indicator-row">
            <div className="indicator-item">
              <label>Max Pain Price:</label>
              <span>{formatValue(maxPain)}</span>
            </div>
            <div className="indicator-item">
              <label>Spot vs Pain:</label>
              <span className="sentiment-badge" style={{ backgroundColor: painSignal.color }}>
                {painSignal.label}
              </span>
            </div>
          </div>
          <div className="indicator-row">
            <div className="indicator-item">
              <label>OI Support (PE):</label>
              <span>{formatValue(analysis.maxPutOISP)}</span>
            </div>
            <div className="indicator-item">
              <label>OI Resistance (CE):</label>
              <span>{formatValue(analysis.maxCallOISP)}</span>
            </div>
          </div>
          <div className="indicator-row">
            <div className="indicator-item">
              <label>OI Range Signal:</label>
              <span className="sentiment-badge" style={{ backgroundColor: rangeSignal.color }}>
                {rangeSignal.label}
              </span>
            </div>
            <div className="indicator-item">
              <label>OI Range:</label>
              <span>{formatValue(oiRangeLow)} — {formatValue(oiRangeHigh)}</span>
            </div>
          </div>
        </div>
      )}

      {/* IV Monitor */}
      {analysis.ivRegime && analysis.ivRegime !== 'No Data' && (
        <div className="indicator-section iv-monitor">
          <h3>IV Monitor</h3>
          <div className="indicator-row">
            <div className="indicator-item">
              <label>ATM CE IV:</label>
              <span>{analysis.atmCallIV != null ? `${analysis.atmCallIV.toFixed(2)}%` : 'N/A'}</span>
            </div>
            <div className="indicator-item">
              <label>ATM PE IV:</label>
              <span>{analysis.atmPutIV != null ? `${analysis.atmPutIV.toFixed(2)}%` : 'N/A'}</span>
            </div>
          </div>
          <div className="indicator-row">
            <div className="indicator-item">
              <label>Avg IV (All Strikes):</label>
              <span>{analysis.avgIV != null ? `${analysis.avgIV.toFixed(2)}%` : 'N/A'}</span>
            </div>
            <div className="indicator-item">
              <label>IV Regime:</label>
              <span
                className="sentiment-badge"
                style={{ backgroundColor: getIVRegimeColor(analysis.ivRegime) }}
              >
                {analysis.ivRegime}
              </span>
            </div>
          </div>
          <div className="indicator-row">
            <div className="indicator-item">
              <label>IV Skew (PE − CE OTM):</label>
              <span>{analysis.ivSkew != null ? `${analysis.ivSkew.toFixed(2)}%` : 'N/A'}</span>
            </div>
            <div className="indicator-item">
              <label>Skew Signal:</label>
              <span className="sentiment-badge" style={{ backgroundColor: ivSkewSignal.color }}>
                {ivSkewSignal.label}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* OI Setup Analysis */}
      {analysis.oiSetupSignal && (
        <div className="indicator-section oi-setup">
          <h3>OI Setup Analysis</h3>
          <div className="indicator-row">
            <div className="indicator-item">
              <label>Call OI (Above Spot):</label>
              <span
                className="sentiment-badge"
                style={{ backgroundColor: analysis.callOISetup === 'Resistance Weakening' ? '#00e676' : '#e53935' }}
              >
                {analysis.callOISetup}
              </span>
            </div>
            <div className="indicator-item">
              <label>Put OI (Below Spot):</label>
              <span
                className="sentiment-badge"
                style={{ backgroundColor: analysis.putOISetup === 'Support Building' ? '#00e676' : '#e53935' }}
              >
                {analysis.putOISetup}
              </span>
            </div>
          </div>
          <div className="indicator-row">
            <div className="indicator-item">
              <label>OI Setup Signal:</label>
              <span
                className="sentiment-badge"
                style={{ backgroundColor: getSetupSignalColor(analysis.oiSetupSignal) }}
              >
                {analysis.oiSetupSignal}
              </span>
            </div>
            <div className="indicator-item" />
          </div>
          <div className="indicator-row">
            <div className="indicator-item">
              <label>ATM CE — Price + OI:</label>
              <span
                className="sentiment-badge"
                style={{ backgroundColor: getMatrixColor(analysis.ceOIMatrix) }}
              >
                {analysis.ceOIMatrix}
              </span>
            </div>
            <div className="indicator-item">
              <label>ATM PE — Price + OI:</label>
              <span
                className="sentiment-badge"
                style={{ backgroundColor: getMatrixColor(analysis.peOIMatrix) }}
              >
                {analysis.peOIMatrix}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndicatorPanel;
